import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Badge, Drawer, Dropdown, Empty, Input, Modal, Popconfirm, Table} from "antd";
import {useSearchParams} from "react-router-dom";
import {useProjectForm} from "../../../providers/ProjectFormProvider";
import {useAuth} from "../../../providers/AuthProvider";
import {ProjectPositionProvider} from "../../../providers/ProjectPositionProvider";
import Form, {FormInputComponent} from "../../../components/form/Form";
import ChangeOrderItemForm from "./ChangeOrderItemForm";
import ChangeOrderItemView from "./ChangeOrderItemView";

const typeLabels = {
    Timeline: 'Timeline Change',
    Rate: 'Rate/Cost Change',
    Candidate: 'Resource Swap'
}
const CreateChangeOrderForm = (props) => {
    const {
        project,
        isLoading,
        isProjectUpdating: isDisabled,
        createChangeOrder,
        updateChangeOrder,
        deleteChangeOrder,
        publishChangeOrder,
        error, refetch,

    } = useProjectForm();
    const {auth, appropriate} = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const changeOrderForm = useRef(null);

    const changeOrderFromParams = useMemo(() => {
        if (!searchParams.has('changeOrder') || searchParams.get('changeOrder') === 'new') {
            return false;
        }
        let changeOrderId = parseInt(searchParams.get('changeOrder'));
        let existing = TB.find(project?.change_orders || [], ['id', changeOrderId]);
        if (existing && existing.latest_contract) {
            existing.contract = existing.latest_contract;
        }
        return existing;
    }, [searchParams.toString(), project, isLoading])

    const [formModel, setFormModel] = useState(() => changeOrderFromParams ? TB.cloneDeep(changeOrderFromParams) : {
        name: `CO-${TB.moment().format('l')}-${TB.replace(project?.name, /\s/g, "")}`,
        notes: '',
        started_at: new Date(),
        ended_at: TB.moment().endOf('week').toDate(),
        items: [],
        contract: null
    })

    const resetFormModel = () => {
        setFormModel({
            name: `CO-${TB.moment().format('l')}-${TB.replace(project?.name, /\s/g, "")}`,
            notes: '',
            started_at: new Date(),
            ended_at: TB.moment().endOf('week').toDate(),
            items: [],
            contract: null
        })
    }

    useEffect(() => {
        if (!isDisabled && !isLoading && changeOrderFromParams && formModel?.id !== changeOrderFromParams?.id) {
            setFormModel(() => TB.cloneDeep(changeOrderFromParams))
        }
    }, [changeOrderFromParams]);


    const handleClose = () => {
        if (isDisabled) {
            return;
        }
        let params = new URLSearchParams(searchParams);
        params.delete('changeOrder');

        return setSearchParams(params);
    }

    const activeEditingRole = useMemo(() => {
        if (!searchParams.has('lineItem')) {
            return false;
        }
        return TB.find(project?.positions, ['id', parseInt(searchParams.get('lineItem'))])
    }, [searchParams.toString()])

    const onOpenDrawer = (positionId) => {
        if (searchParams.has('lineItem')) {
            return;
        }
        let params = new URLSearchParams(searchParams);
        params.set('lineItem', positionId)
        return setSearchParams(params)
    }

    const onCloseDrawer = async () => {
        let params = new URLSearchParams(searchParams);
        params.delete('lineItem')
        await setSearchParams(params)
    }

    const updateChangeOrderItems = async (items) => {
        let changeOrder;
        if (!formModel.id) {
            changeOrder = await createChangeOrder({...formModel, items});
            if (changeOrder) {
                TB.toast('Change Order Updated', {theme: 'success'});
                let params = new URLSearchParams(searchParams);
                params.delete('lineItem')
                params.set('changeOrder', changeOrder.id);
                await setSearchParams(params)
                return true;
            }
        } else {
            changeOrder = await updateChangeOrder(formModel.id, {...formModel, items});
            if (changeOrder) {
                if (changeOrder.latest_contract) {
                    changeOrder.contract = changeOrder.latest_contract
                }


                await onCloseDrawer()
                TB.toast('Change Order Updated', {theme: 'success'})
                await setFormModel(changeOrder);
                return true
            }
        }
        return false;
    }
    const onUpdateLineItem = async (item) => {
        let items = [...formModel.items];
        if (item.id) {
            items = TB.map(items, lineItem => {
                if (lineItem?.id === item.id) {
                    return {
                        ...item
                    }
                }
                return lineItem
            })
        } else {
            items.push(item);
        }
        return updateChangeOrderItems(items)
    }

    const changeOrderColumnWidths = {
        Role: 200,
        // Type: 120,
        EndedAt: 180,
        HourlyRate: 150,
        PercentMarkup: auth.canManageClients() ? 130 : 0,
        HourImpact: 90,
        ResourceCostImpact: auth.canManageClients() ? 125 : 0,
        ClientFeeImpact: 125,
        Actions: (auth.canManageClients() && !formModel.published_at) ? 110 : 80
    }

    if (!project) return null;

    return (
        <React.Fragment>
            <Modal
                okText="Submit"
                title={changeOrderFromParams ? "Edit Change Order" : "Create Change Order"}
                open={searchParams.has('changeOrder')}
                closable={true}
                maskClosable={false}
                className="change-order-form-container"
                // style={{top: 50}}
                // destroyOnClose={true}
                onCancel={async () => {
                    await resetFormModel();
                    return handleClose()
                }}
                zIndex={199}
                width={1200}
                footer={null}
            >
                <div className="py-3 px-5 flex flex-col items-stretch">
                    <Form
                        ref={changeOrderForm}
                        showRequired
                        // minimal
                        background={'transparent'}
                        title={null}
                        subtitle={null}
                        submitButton={false}
                        onSubmit={null}
                        model={formModel}
                        onChange={(model) => {
                            setFormModel({...formModel, ...model})
                        }}
                        isDisabled={isDisabled}
                        fields={[]}
                    >
                        <div className="flex flex-col items-stretch gap-4">
                            <div className="flex flex-row gap-4">
                                <div className="basis-1/3">
                                    <FormInputComponent
                                        fieldName="name"
                                        value={formModel.name || ''}
                                        disabled={isDisabled}
                                        required={true}
                                        maxLength={50}
                                        label="CO Name:"
                                        type="antd-input">

                                        <Input
                                            readOnly={!auth.canManageClients()}
                                            bordered={auth.canManageClients()}
                                        />

                                    </FormInputComponent>
                                </div>
                                <div className="flex-1">
                                    <FormInputComponent
                                        fieldName="started_at"
                                        value={formModel.started_at}
                                        type="date"
                                        required
                                        label="CO Start Date"
                                        disabled={isDisabled}
                                        readOnly={(formModel.published_at && !formModel.is_latest) || !auth.canManageClients()}
                                        bordered={(!formModel.published_at || formModel.is_latest) && auth.canManageClients()}
                                        minDate={project.anticipatedStart() ? project.anticipatedStart()
                                            .endOf('week')
                                            .toDate() : undefined}
                                        validationErrors={{
                                            isDefaultRequiredValue: TB.trans('validation.required', {
                                                'attribute': 'start date',
                                            })
                                        }}/>

                                </div>
                                <div className="flex-1">
                                    <FormInputComponent
                                        fieldName="ended_at"
                                        value={formModel.ended_at}
                                        type="date"
                                        label="CO End Date"
                                        required
                                        readOnly={(formModel.published_at && !formModel.is_latest) || !auth.canManageClients()}
                                        bordered={(!formModel.published_at || formModel.is_latest) && auth.canManageClients()}
                                        disabled={isDisabled}
                                        minDate={formModel.started_at ? formModel.started_at : undefined}
                                        validationErrors={{
                                            isDefaultRequiredValue: TB.trans('validation.required', {
                                                'attribute': 'end date',
                                            })
                                        }}/>

                                </div>
                                <div className="flex-1">
                                    <div className="transition-all ease-in flex flex-col items-start form-group">
                                        <label className="col-form-label">
                                            Status
                                        </label>

                                        <Badge color={changeOrderFromParams ? (
                                            formModel.published_at ? TB.config('color.success-dark') : TB.config('color.info')
                                        ) : TB.config('color.warning-dark')} count={changeOrderFromParams ? (
                                            formModel.published_at ? 'Published' : 'Draft'
                                        ) : 'New'}/>
                                    </div>

                                </div>
                            </div>

                            <div className="flex flex-row gap-4">

                                <FormInputComponent
                                    fieldName="notes"
                                    value={formModel.notes}
                                    disabled={isDisabled}
                                    required={false}
                                    label="Notes:"
                                    className={TB.mapClass("flex-grow max-w-[600px]", {
                                        "pointer-events-none": (formModel.published_at && !formModel.is_latest) || !auth.canManageClients()
                                    })}
                                    type="antd-input"
                                >

                                    <Input.TextArea
                                        readOnly={!auth.canManageClients()}
                                        bordered={formModel.notes || auth.canManageClients()}
                                        placeholder={((formModel.published_at && !formModel.is_latest) || !auth.canManageClients()) ? "No notes provided..." : "Describe the reason for this change order..."}
                                        autoSize={{minRows: 4}}
                                    />
                                </FormInputComponent>

                                {auth.canManageClients() && (
                                    <FormInputComponent
                                        fieldName="contract"
                                        required={false}
                                        value={formModel.contract}
                                        type="file"
                                        readOnly={!auth.canManageClients()}
                                        disabled={isDisabled}
                                        size={formModel?.contract?.size}
                                        viewable={formModel?.contract?.id}
                                        label={<p className="text-bold">Change Order Document</p>}
                                        fileNameDisplay={(fileData) => {
                                            if (typeof formModel?.contract === 'string') {
                                                return formModel?.contract;
                                            }
                                            return formModel?.contract?.name || fileData.name;
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                    </Form>

                    <div className="flex flex-col mt-6 gap-6">

                        <div>
                            <div className="flex flex-row gap-3 mb-3 items-center">
                                <p>
                                    Change Order Items
                                </p>
                                {(!formModel.published_at || formModel.is_latest) && auth.canManageClients() && (
                                    <Dropdown
                                        menu={{
                                            items: TB.map(project.positions, pos => ({
                                                label: pos.title,
                                                key: pos.id,
                                                disabled: !!TB.find(formModel.items, ['position_id', pos.id]) || !!TB.find(formModel.items, ['position_id', pos.id])
                                            })),
                                            onClick: async ({key}) => !activeEditingRole && onOpenDrawer(key)
                                        }}
                                        trigger={['click']}
                                        placement="bottomRight"
                                        disabled={isDisabled}
                                    >
                                        <button className="btn btn-primary text-xs px-2 py-1.5 w-32" disabled={isDisabled}>
                                            Add Line Item
                                        </button>
                                    </Dropdown>
                                )}
                            </div>
                            {formModel.items?.length > 0 ? (
                                <div className="flex flex-col gap-3">
                                    <Table
                                        rowKey="id"
                                        dataSource={formModel.items}
                                        bordered
                                        size="small"
                                        scroll={{
                                            x: TB.sum(TB.values(changeOrderColumnWidths)),
                                        }}
                                        summary={(pageData) => {
                                            let totalResourceCostImpact = TB.sumBy(pageData, `net_talent_cost`);
                                            let totalPlatformCostImpact = TB.sumBy(pageData, `net_contract_value`);
                                            let totalPlatformFeeImpact = TB.sumBy(pageData, item => item.net_contract_value - item.net_talent_cost);
                                            let totalHourImpact = TB.sumBy(pageData, item => item.new_total_periods - item.original_total_periods);

                                            return (
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0}/>
                                                    <Table.Summary.Cell index={1}/>
                                                    {auth.canManageClients() && <Table.Summary.Cell index={2}/>}
                                                    <Table.Summary.Cell index={auth.canManageClients() ? 3 : 2}>
                                                        <p className="text-xxs text-right font-medium">
                                                            Totals
                                                        </p>
                                                    </Table.Summary.Cell>

                                                    <Table.Summary.Cell index={auth.canManageClients() ? 4 : 5}>
                                                        <p className={TB.mapClass("text-xxs font-medium", {"text-danger": totalHourImpact < 0})}>
                                                            {totalHourImpact} hrs
                                                        </p>
                                                    </Table.Summary.Cell>
                                                    {auth.canManageClients() && (
                                                        <Table.Summary.Cell index={5}>
                                                            <p className={TB.mapClass("text-xxs font-medium", {"text-danger": totalResourceCostImpact < 0})}>
                                                                {TB.formatCurrency(totalResourceCostImpact, false)}
                                                            </p>
                                                        </Table.Summary.Cell>
                                                    )}
                                                    <Table.Summary.Cell index={auth.canManageClients() ? 6 : 4}>
                                                        <p className={TB.mapClass("text-xxs font-medium", {"text-danger": totalPlatformCostImpact < 0})}>
                                                            {TB.formatCurrency(totalPlatformCostImpact, false)}
                                                        </p>
                                                    </Table.Summary.Cell>

                                                    {(formModel.published_at || auth.canManageClients()) && (
                                                        <Table.Summary.Cell index={auth.canManageClients() ? 7 : 5}/>
                                                    )}
                                                </Table.Summary.Row>
                                            );
                                        }}
                                        pagination={false}
                                    >
                                        <Table.Column
                                            title={() => <p className="text-xxs font-semibold">Position</p>}
                                            width={changeOrderColumnWidths.Role}
                                            dataIndex={'position_id'}
                                            key={'position_id'}
                                            render={(position_id, record) => {
                                                let position = TB.find(project.positions, ['id', position_id])?.title;
                                                if (!position) {
                                                    return 'N/A'
                                                }
                                                return (
                                                    <div className="flex flex-col items-start gap-1">
                                                        <p className="text-xxs whitespace-no-wrap">
                                                            {position}
                                                        </p>
                                                        <p className="text-xxs text-secondary">
                                                            {typeLabels[record.type]}
                                                        </p>
                                                    </div>
                                                )
                                            }}
                                            fixed={'left'}

                                        />
                                        {/*<Table.Column*/}
                                        {/*    title={() => <p className="text-xxs font-semibold">Type</p>}*/}
                                        {/*    width={changeOrderColumnWidths.Type}*/}
                                        {/*    dataIndex={'type'}*/}
                                        {/*    key={'type'}*/}
                                        {/*    render={type => (*/}
                                        {/*        <p className="text-xxs text-secondary">*/}
                                        {/*            {typeLabels[type]}*/}
                                        {/*        </p>*/}
                                        {/*    )}*/}

                                        {/*/>*/}
                                        <Table.Column

                                            title={() => <p className="text-xxs font-semibold">Ended At</p>}
                                            dataIndex={'new_ended_at'}
                                            key={'new_ended_at'}
                                            width={changeOrderColumnWidths.EndedAt}
                                            render={(new_ended_at, record) => (
                                                <p className="text-xxs">
                                                    <span className="flex flex-row items-center gap-1.5">
                                                        {!TB.moment(new_ended_at).isSame(record.original_ended_at, 'day') && (
                                                            <span className="text-danger line-through">
                                                                {TB.moment(record.original_ended_at).format('ll')}
                                                            </span>
                                                        )}
                                                        <span>
                                                            {TB.moment(new_ended_at).format('ll')}
                                                        </span>
                                                    </span>
                                                </p>
                                            )}

                                        />
                                        {auth.canManageClients() ? (
                                            <Table.Column
                                                title={() => <p className="text-xxs font-semibold">Hourly Rate</p>}
                                                dataIndex={'new_rate'}
                                                key={'new_rate'}
                                                width={changeOrderColumnWidths.HourlyRate}
                                                render={(new_rate, record) => (
                                                    <p className="text-xxs">
                                                        <span className="flex flex-row items-center gap-1.5">
                                                            {new_rate !== record.original_rate && (
                                                                <span className="text-danger line-through">
                                                                    {TB.formatCurrency(record.original_rate, false)}
                                                                </span>
                                                            )}
                                                            <span>
                                                                {TB.formatCurrency(new_rate, false)}
                                                            </span>
                                                        </span>

                                                    </p>
                                                )}
                                            />
                                        ) : (
                                            <Table.Column
                                                title={() => <p className="text-xxs font-semibold">Hourly Rate</p>}
                                                dataIndex={'new_platform_rate'}
                                                key={'new_platform_rate'}
                                                width={changeOrderColumnWidths.HourlyRate}
                                                render={(new_platform_rate, record) => (
                                                    <p className="text-xxs">
                                                        <span className="flex flex-row items-center gap-1.5">
                                                            {new_platform_rate !== record.original_platform_rate && (
                                                                <span className="text-danger line-through">
                                                                    {TB.formatCurrency(record.original_platform_rate, false)}
                                                                </span>
                                                            )}
                                                            <span>
                                                                {TB.formatCurrency(new_platform_rate, false)}
                                                            </span>
                                                        </span>

                                                    </p>
                                                )}
                                            />
                                        )}

                                        {auth.canManageClients() && (
                                            <Table.Column
                                                title={() => <p className="text-xxs font-semibold">Percent Markup</p>}
                                                dataIndex={'new_percent_markup'}
                                                key={'new_percent_markup'}
                                                width={changeOrderColumnWidths.PercentMarkup}
                                                render={(new_percent_markup, record) => (
                                                    <p className="text-xs">
                                                        <span className="flex flex-row items-center gap-1.5">
                                                            {new_percent_markup !== record.original_percent_markup && (
                                                                <span className="text-danger line-through">
                                                                    {TB.formatNumber(record.original_percent_markup * 100, 2)}%
                                                                </span>
                                                            )}
                                                            <span>
                                                                {TB.formatNumber(new_percent_markup * 100, 2)}%
                                                            </span>
                                                        </span>
                                                    </p>
                                                )}
                                            />
                                        )}
                                        <Table.ColumnGroup
                                            title={() => <p className="text-xxs font-semibold">Impact</p>}
                                            key="id"
                                        >
                                            <Table.Column
                                                title={() => <p className="text-xxs font-semibold">Hours</p>}
                                                dataIndex={'new_total_periods'}
                                                key={'new_total_periods'}
                                                width={changeOrderColumnWidths.HourImpact}
                                                render={(_, record) => (
                                                    <p className={TB.mapClass("text-xxs", {"text-danger": record.new_total_periods - record.original_total_periods < 0})}>
                                                        {record.new_total_periods - record.original_total_periods} hrs
                                                    </p>
                                                )}
                                            />
                                            {auth.canManageClients() && (
                                                <Table.Column
                                                    title={() => <p className="text-xxs font-semibold">Resource Cost</p>}
                                                    dataIndex={'net_talent_cost'}
                                                    key={'net_talent_cost'}
                                                    width={changeOrderColumnWidths.ResourceCostImpact}
                                                    render={net_talent_cost => (
                                                        <p className={TB.mapClass("text-xxs", {"text-danger": net_talent_cost < 0})}>
                                                            {TB.formatCurrency(net_talent_cost, false)}
                                                        </p>
                                                    )}
                                                />
                                            )}

                                            <Table.Column

                                                title={() => <p className="text-xxs font-semibold">{auth.canManageClients() ? "Client Fee" : "Contract Value"}</p>}
                                                dataIndex={'net_contract_value'}
                                                key={'net_contract_value'}
                                                width={changeOrderColumnWidths.ClientFeeImpact}
                                                render={net_contract_value => (
                                                    <p className={TB.mapClass("text-xxs", {"text-danger": net_contract_value < 0})}>
                                                        {TB.formatCurrency(net_contract_value, false)}
                                                    </p>
                                                )}
                                            />
                                        </Table.ColumnGroup>
                                        <Table.Column
                                            title={() => <p className="text-xxs font-semibold"></p>}
                                            dataIndex={'id'}
                                            key={'id'}
                                            width={changeOrderColumnWidths.Actions}
                                            render={(_, record) => (
                                                <div className="flex flex-row items-center gap-1.5">
                                                    {auth.canManageClients() && (!formModel.published_at) && (
                                                        <Popconfirm className="popup-confirmation"
                                                                    title="Are you sure to delete this item?"
                                                                    onConfirm={async () => {
                                                                        let items = TB.filter(formModel.items || [], item => item?.id !== record.id);
                                                                        return updateChangeOrderItems(items)
                                                                    }}
                                                                    onCancel={() => console.log("cancelled")}
                                                                    okText="Yes" cancelText="No">
                                                            <button disabled={isDisabled}
                                                                    className="btn-link btn-link-danger text-xs"> Remove
                                                            </button>
                                                        </Popconfirm>
                                                    )}
                                                    <button disabled={isDisabled}
                                                            onClick={() => {
                                                                return onOpenDrawer(record.position_id)
                                                            }}
                                                            className="btn-link btn-link-primary text-xs">
                                                        View
                                                    </button>
                                                </div>
                                            )}

                                        />

                                    </Table>
                                    {/*{TB.map(formModel.items, (item, itemIndex) => {*/}
                                    {/*    return <div key={`item-${itemIndex}`}>*/}
                                    {/*        <code>*/}
                                    {/*            {JSON.stringify(item)}*/}
                                    {/*        </code>*/}
                                    {/*    </div>*/}
                                    {/*})}*/}

                                </div>
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={"Add line items for affected resources"}
                                />
                            )}
                        </div>

                        <div className="flex flex-row px-4 mt-5 items-center gap-3 justify-end">



                            {(formModel.published_at || !auth.canManageClients()) ? (
                                <React.Fragment>
                                    <button
                                        type="button" className="btn btn-outline-dark"
                                        disabled={isDisabled}
                                        onClick={async () => {
                                            await handleClose();
                                            await resetFormModel();
                                        }}
                                    >
                                        Close
                                    </button>

                                    {auth.canManageClients() && (
                                        <React.Fragment>
                                            <button
                                                type="button" className="btn btn-primary"
                                                disabled={isDisabled || !formModel.items?.length}
                                                onClick={async () => {
                                                    let updatedChangeOrder = {...formModel}
                                                    if (!updatedChangeOrder.contract) {
                                                        updatedChangeOrder.contract = null
                                                    }
                                                    delete updatedChangeOrder.items
                                                    let changeOrder = await updateChangeOrder(formModel.id, updatedChangeOrder);
                                                    if (changeOrder) {
                                                        TB.toast('Published Change Order Updated', {theme: 'success'})

                                                        await handleClose();
                                                        await resetFormModel();
                                                    }
                                                }}>
                                                Update
                                            </button>
                                            {formModel.is_latest && (
                                                <button
                                                    type="button" className="btn btn-danger"
                                                    disabled={isDisabled}
                                                    onClick={async () => {
                                                        let confirm = await TB.alert('alert', 'Are you sure?',
                                                            'Are you sure you want to delete and rollback this Change Order? Note: this cannot be undone', [
                                                                {title: 'Yes, Continue'},
                                                                {title: 'Cancel'}
                                                            ]
                                                        )
                                                        if (confirm) {
                                                            let response = await deleteChangeOrder(formModel.id);
                                                            if (response) {
                                                                TB.toast('Published Change Order Deleted', {theme: 'success'})

                                                                await handleClose();
                                                                await resetFormModel();
                                                            }
                                                        }
                                                    }}>
                                                    Delete Change Order
                                                </button>
                                            )}
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            ) : (formModel.id ? (
                                <React.Fragment>
                                    <button
                                        type="button" className="btn btn-dark"
                                        onClick={async () => {
                                            await handleClose();
                                            await resetFormModel();
                                        }}
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="button" className="btn btn-outline btn-outline-danger"
                                        disabled={isDisabled || !formModel.items?.length}
                                        onClick={async () => {
                                            let confirm = await TB.alert('alert', 'Are you sure?',
                                                'Are you sure you want to continue and discard this Change Order? Note: this cannot be undone', [
                                                    {title: 'Yes, Continue'},
                                                    {title: 'Cancel'}
                                                ]
                                            )
                                            if (confirm) {
                                                let response = await deleteChangeOrder(formModel.id);
                                                if (response) {
                                                    TB.toast('Change Order Draft Deleted', {theme: 'success'})

                                                    await handleClose();
                                                    await resetFormModel();
                                                }
                                            }

                                        }}
                                    >
                                        Discard Draft
                                    </button>
                                    <button
                                        type="button" className="btn btn-outline btn-outline-primary"
                                        disabled={isDisabled || !formModel.items?.length}
                                        onClick={async () => {
                                            let updatedChangeOrder = {...formModel}
                                            if (!updatedChangeOrder.contract) {
                                                updatedChangeOrder.contract = null
                                            }
                                            delete updatedChangeOrder.items
                                            let changeOrder = await updateChangeOrder(formModel.id, updatedChangeOrder);
                                            if (changeOrder) {
                                                if (changeOrder.latest_contract) {
                                                    changeOrder.contract = changeOrder.latest_contract
                                                }
                                                TB.toast('Change Order Updated', {theme: 'success'})
                                                await setFormModel(changeOrder);
                                            }
                                        }}>
                                        Update
                                    </button>
                                    <button
                                        type="button" className="btn btn-primary"
                                        disabled={isDisabled || !formModel.items?.length}
                                        onClick={async () => {
                                            let response = await publishChangeOrder(formModel.id);
                                            if (response) {
                                                TB.toast('Change Order Published', {theme: 'success'})

                                                await handleClose();
                                                await resetFormModel();
                                            }
                                        }}
                                    >
                                        Publish
                                    </button>
                                </React.Fragment>
                            ) : (
                                <button
                                    type="button" className="btn btn-outline-dark"
                                    onClick={async () => {
                                        await handleClose();
                                        await resetFormModel();
                                    }}
                                >
                                    Cancel
                                </button>
                            ))}

                        </div>
                    </div>
                </div>

            </Modal>
            {searchParams.has('changeOrder') && (
                <Drawer
                    title={(
                        <div className="px-2 py-1.5">
                            <p>
                                &nbsp;
                                Change Order Item for Position:
                                &nbsp;
                                {activeEditingRole && (
                                    <span className="font-semibold">{activeEditingRole?.title}</span>
                                )}
                            </p>
                        </div>
                    )}
                    width={1200}
                    onClose={onCloseDrawer}
                    open={searchParams?.has('lineItem')}
                >
                    {activeEditingRole && (
                        <ProjectPositionProvider positionId={activeEditingRole?.id}>
                            {((TB.find(formModel.items, ['position_id', parseInt(searchParams?.get('lineItem'))]) || formModel.published_at || !auth.canManageClients())) ? (
                                    <ChangeOrderItemView
                                        isDisabled={isDisabled}
                                        order={formModel}
                                        onClose={onCloseDrawer}
                                        onSubmit={onUpdateLineItem}/>
                                ) : (
                                    <ChangeOrderItemForm
                                        isDisabled={isDisabled}
                                        order={formModel}
                                        onClose={onCloseDrawer}
                                        onSubmit={onUpdateLineItem}/>
                                )
                            }
                        </ProjectPositionProvider>
                    )}
                </Drawer>
            )}
        </React.Fragment>
    )
};

export default CreateChangeOrderForm;